import {
  CapabilityFeatureDefinition,
  FEATURE_TO_PERMISSION_KEY,
  FeatureWithPermissionMapping,
  isFeatureWithPermissionMappingDefinition,
  ItemWithPermissions,
} from 'auth/capabilities/CapabilityFeatureConstants';
import isCapabilityFeatureEnabledFromBenchlingConfig from 'auth/capabilities/isCapabilityFeatureEnabledFromBenchlingConfig';
import {Feature} from 'auth/feature-enablement/FeatureConstants';
import {getActiveShareLinkItemId} from 'ShareAccess';
import * as logging from 'util/Logging';

// Right now we do not support updates to the list of enabled features, so we can just cache them here in this
// private variable for fast lookups. It is an empty array if capability gating is not enabled.
let enabledCapabilityFeatures: Set<Feature> | null = makeEnabledCapabilityFeaturesSetFromBootstrap();
let shareLinkEnabledFeatures: Set<Feature> | null = makeShareLinkEnabledFeaturesFromBootstrap();

// Exported so tests can control the set of enabled features without needing to override
// window.bootstrap.enabledCapabilityFeatures before this module loads which was flaky.
export function _setEnabledCapabilityFeatures(features: Set<Feature>): void {
  enabledCapabilityFeatures = features;
}
export function _setEnabledShareLinkFeatures(features: Set<Feature>): void {
  shareLinkEnabledFeatures = features;
}

function makeEnabledCapabilityFeaturesSetFromBootstrap(): Set<Feature> | null {
  const enabledCapabilityFeatures =
    window.bootstrap?.enabledCapabilityFeatures || window.bootstrap?.enabledFeatures;
  if (!enabledCapabilityFeatures) {
    if (window.debug) {
      logging.warn(
        'This page has not opted-in to front-end capability management gating. To enable capability ' +
          'managment for this page\'s front-end components, please set "window.bootstrap.enabledCapabilityFeatures" -' +
          ' see benchling/lib/sharedviews.py for an example. Note that not all pages need capability ' +
          'management for its front-end components so this is not an indication of anything wrong.'
      );
    }
    return null;
  }
  return new Set(enabledCapabilityFeatures);
}

function makeShareLinkEnabledFeaturesFromBootstrap(): Set<Feature> | null {
  if (!window.bootstrap?.shareLinkEnabledFeatures) {
    return null;
  }
  return new Set(window.bootstrap.shareLinkEnabledFeatures);
}

export function isFeatureEnabledFromShareLink(featureDefinition: CapabilityFeatureDefinition): boolean {
  if (featureDefinition.item && featureDefinition.item.id === getActiveShareLinkItemId()) {
    // Only a subset of features should ever be enabled when an item is opened via sharelinks. They will
    // likely be 1:1 with READ features but we read a list from the server to keep a single source of truth
    // and keep the front-end decoupled from access levels.
    return (shareLinkEnabledFeatures ?? new Set()).has(featureDefinition.feature);
  }

  return false;
}

export function isFeatureEnabledFromFeatureList(feature: Feature): boolean {
  // If "enabledCapabilityFeatures" is not in the page bootstrap (or window.bootstrap doesn't exist) the page has not
  // opted-in to using capability management gating for its FE components so we simply return true for all
  // features being checked. We can safely use an opt-in model because all the FE components that use CM are
  // exclusively used with the main "editor" template. If gating requirements are added for routes that
  // use other templates, those templates should opt-in to CM by adding "enabledCapabilityFeatures" to the page
  // bootstrap.
  if (!enabledCapabilityFeatures) {
    return true;
  }

  return enabledCapabilityFeatures.has(feature);
}

export function isFeatureEnabledFromPermissions(
  feature: FeatureWithPermissionMapping,
  item: ItemWithPermissions
): boolean {
  // @ts-expect-error TODO: resolve type safety between Permissions and PermissionsGraphQL
  return Boolean(item.permissions[FEATURE_TO_PERMISSION_KEY[feature]]);
}

export function legacyIsFeatureEnabled(
  featureDefinition: CapabilityFeatureDefinition,
  isSharedContext: boolean = false
): boolean {
  if (
    isFeatureWithPermissionMappingDefinition(featureDefinition) &&
    featureDefinition.item?.permissions &&
    !isFeatureEnabledFromPermissions(featureDefinition.feature, featureDefinition.item)
  ) {
    return false;
  }
  // We always return true for shared features if the gating framework is not enabled because the legacy
  // tenant-wide gating logic never gates shared features. Gating for shared features (like the expanded
  // search listing toolbar buttons) is only done at the item level.
  return isSharedContext || isCapabilityFeatureEnabledFromBenchlingConfig(featureDefinition.feature);
}
